import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import api from "../../Services/api";
import "./FormsList.css"; 

const FormsListHeaderComponent = ({
  globalFilterValue,
  onGlobalFilterChange,
  clearFilter,
  onFormsUploaded,
}) => {
  const toast = useRef(null);
  const fileUploadRef = useRef(null);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async ({ files }) => {
    if (!files || !files.length) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "No file selected",
        life: 3000,
      });
      return;
    }

    setUploading(true);
    const file = files[0];

    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      toast.current.show({
        severity: "error",
        summary: "Invalid File",
        detail: "Please upload only JSON files",
        life: 3000,
      });
      setUploading(false);
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
      return;
    }

    try {
      const fileContent = await readFileAsText(file);
      let jsonData;

      try {
        jsonData = JSON.parse(fileContent);
      } catch (parseError) {
        toast.current.show({
          severity: "error",
          summary: "Invalid JSON",
          detail: "The selected file contains invalid JSON format",
          life: 3000,
        });
        setUploading(false);
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
        return;
      }

      const response = await api.post("/forms/import", jsonData);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Forms uploaded successfully",
        life: 3000,
      });

      if (onFormsUploaded && response.data) {
        const uploadedForms = response.data.form
          ? [response.data.form]
          : Array.isArray(response.data)
            ? response.data
            : [];
        onFormsUploaded(uploadedForms);

        setTimeout(() => {
          if (uploadedForms.length) {
            onFormsUploaded(uploadedForms);
          }
        }, 100);
      }

      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
    } catch (error) {
      console.error("Upload error:", error);

      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        setValidationErrors(error.response.data.errors);
        setErrorDialogVisible(true);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Upload Failed",
          detail: error.response?.data?.message || "Error uploading file",
          life: 3000,
        });
      }

      // Always clear the file input on error
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
    } finally {
      setUploading(false);
    }
  };

  // Helper function to read file as text
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const errorDialogFooter = (
    <div>
      <Button
        label="Close"
        icon="pi pi-times"
        onClick={() => setErrorDialogVisible(false)}
        className="p-button-text"
      />
    </div>
  );

  return (
    <div className="flex justify-content-between form-list-header">
      <Toast ref={toast} />
      <Dialog
        header="JSON Validation Errors"
        visible={errorDialogVisible}
        style={{
          width: window.innerWidth > 768 ? "80vw" : "98vw",
          height: window.innerWidth > 768 ? "80vh" : "95vh",
          maxWidth: window.innerWidth > 768 ? "none" : "98vw",
        }}
        modal
        footer={errorDialogFooter}
        onHide={() => setErrorDialogVisible(false)}
      >
        {validationErrors.length > 0 && (
          <div>
            <p className="text-lg font-bold mb-3">
              Please correct the following errors in your JSON file:
            </p>
            <div
              className="border-1 surface-border border-round p-3"
              style={{ maxHeight: "500px", overflow: "auto" }}
            >
              <ul className="list-none p-0 m-0">
                {validationErrors.map((error, index) => (
                  <li
                    key={index}
                    className="p-2 border-bottom-1 surface-border flex align-items-center"
                  >
                    <i className="pi pi-exclamation-circle text-red-500 mr-2"></i>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Dialog>
      <div className="flex search-container">
        <IconField iconPosition="right" className="search-field">
          <InputIcon className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            style={{ fontFamily: "poppins" }}
            className="search-input"
          />
        </IconField>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={clearFilter}
          className="clear-button"
          style={{
            backgroundColor: "#D97A07",
            border: "none",
            color: "#fff",
            marginLeft: "10px",
            fontFamily: "poppins",
          }}
        />
        <FileUpload
          ref={fileUploadRef}
          mode="basic"
          name="forms[]"
          accept=".json"
          maxFileSize={10000000}
          customUpload={true}
          uploadHandler={handleUpload}
          auto
          chooseLabel={uploading ? "Uploading..." : "Upload"}
          className="upload-button"
          style={{
            marginLeft: "5px",
          }}
          disabled={uploading}
          chooseOptions={{
            icon: uploading ? "pi pi-spin pi-spinner" : "pi pi-upload",
            style: {
              backgroundColor: "#0d2e4d",
              border: "none",
              color: "#fff",
              fontFamily: "poppins",
              opacity: uploading ? 0.7 : 1,
            },
          }}
        />
      </div>
    </div>
  );
};

export default FormsListHeaderComponent;
